import { AnimatePresence, motion } from 'framer-motion'
import {
  Activity,
  BarChart3,
  Briefcase,
  ChevronRight,
  Clock,
  Database,
  FileText,
  LayoutDashboard,
  MessageSquare,
  Search,
  Shield,
  TrendingUp,
  Users
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Badge } from './ui/badge'

const Sidebar = ({ isOpen, currentUser }) => {
  const location = useLocation()
  const [expandedSections, setExpandedSections] = useState({
    main: true,
    tools: true,
    management: true,
    admin: currentUser?.role === 'super_admin' || currentUser?.role === 'admin' || currentUser?.role === 'developer'
  })
  const [sidebarStats, setSidebarStats] = useState({
    pendingCases: 0,
    monthlySearches: 0,
    systemStatus: '正常'
  })

  // 載入側邊欄統計數據
  useEffect(() => {
    const fetchSidebarStats = async () => {
      try {
        const response = await fetch('/api/stats/dashboard')
        if (response.ok) {
          const result = await response.json()
          const data = result.data || result // 處理嵌套的 data 結構
          setSidebarStats({
            pendingCases: data.pendingDeadlines || 0,
            monthlySearches: data.monthlySearches || 0,
            systemStatus: '正常'
          })
        }
      } catch (error) {
        console.error('載入側邊欄統計失敗:', error)
        // 保持預設值 0
      }
    }

    // 初始載入
    fetchSidebarStats()
    
    // 每30秒自動刷新一次
    const interval = setInterval(fetchSidebarStats, 30000)
    
    // 清理定時器
    return () => clearInterval(interval)
  }, [])

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }))
  }

  // 權限檢查函數
  const hasPermission = (requiredRoles) => {
    if (!currentUser || !requiredRoles) return true
    return requiredRoles.includes(currentUser.role)
  }

  // 獲取用戶角色顯示名稱
  const getUserRoleDisplay = (role) => {
    const roleMap = {
      'super_admin': '超級管理員',
      'admin': '管理員',
      'developer': '開發人員',
      'senior_attorney': '資深專利律師',
      'attorney': '專利律師',
      'assistant': '助理',
      'user': '用戶'
    }
    return roleMap[role] || role
  }

  const menuSections = [
    {
      id: 'main',
      title: '主要功能',
      items: [
        // 儀表板 - 只有管理員或有權限的用戶可見
        ...(currentUser?.can_access_dashboard ? [{
          path: '/dashboard',
          icon: LayoutDashboard,
          label: '儀表板',
          badge: null
        }] : []),
        {
          path: '/patent-search',
          icon: Search,
          label: '專利檢索',
          badge: null
        },
        {
          path: '/document-analysis',
          icon: FileText,
          label: '文件分析',
          badge: null
        },
        {
          path: '/case-management',
          icon: Briefcase,
          label: '案件管理',
          badge: null
        },
        {
          path: '/patent-database-comparison',
          icon: Database,
          label: '台灣外國專利比對',
          badge: null
        }
      ]
    },
    {
      id: 'tools',
      title: '工具與報告',
      items: [
        {
          path: '/knowledge-base',
          icon: Database,
          label: '知識庫管理',
          badge: null
        },
        {
          path: '/rag-chat',
          icon: MessageSquare,
          label: 'AI智能問答',
          badge: 'RAG'
        },
        {
          path: '/reports',
          icon: BarChart3,
          label: '報告分析',
          badge: null
        }
      ]
    },
    // 管理員專用區域
    ...(hasPermission(['super_admin', 'admin', 'developer']) ? [{
      id: 'admin',
      title: '系統管理',
      items: [
        // API健康檢查 - 只有管理員或有權限的用戶可見
        ...(currentUser?.can_access_health_check ? [{
          path: '/health',
          icon: Activity,
          label: 'API健康檢查',
          badge: 'LIVE'
        }] : []),
        {
          path: '/admin/users',
          icon: Users,
          label: '用戶管理',
          badge: currentUser?.role === 'super_admin' ? 'ADMIN' : null,
          requiredRoles: ['super_admin', 'admin', 'developer']
        }
      ].filter(item => !item.requiredRoles || hasPermission(item.requiredRoles))
    }] : [])
  ]

  const quickStats = [
    { icon: Clock, label: '即將到期', value: sidebarStats.pendingCases.toString(), color: 'text-orange-600 dark:text-orange-400' },
    { icon: TrendingUp, label: '本月搜尋', value: sidebarStats.monthlySearches.toString(), color: 'text-green-600 dark:text-green-400' },
    { icon: Shield, label: '系統狀態', value: sidebarStats.systemStatus, color: 'text-blue-600 dark:text-blue-400' }
  ]

  const isActive = (path) => {
    return location.pathname === path
  }

  const SidebarItem = ({ item, isOpen }) => {
    const active = isActive(item.path)

    return (
      <Link to={item.path}>
        <motion.div
          className={`
            flex items-center p-3 rounded-lg transition-all duration-200 group cursor-pointer
            ${active
              ? 'bg-blue-50 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 border-r-2 border-blue-600'
              : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-100'
            }
          `}
          whileHover={{ x: 2 }}
          whileTap={{ scale: 0.98 }}
        >
          <item.icon className={`h-5 w-5 ${active ? 'text-blue-600 dark:text-blue-400' : 'text-gray-500 dark:text-gray-400'} group-hover:text-gray-700 dark:group-hover:text-gray-300`} />

          <AnimatePresence>
            {isOpen && (
              <motion.div
                initial={{ opacity: 0, width: 0 }}
                animate={{ opacity: 1, width: 'auto' }}
                exit={{ opacity: 0, width: 0 }}
                transition={{ duration: 0.2 }}
                className="ml-3 flex-1 min-w-0"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium truncate ${active ? 'text-blue-700 dark:text-blue-300' : 'text-gray-900 dark:text-gray-100'}`}>
                      {item.label}
                    </p>
                  </div>
                  {item.badge && (
                    <Badge
                      variant={item.badge === 'AI' ? 'default' : item.badge === 'NEW' ? 'destructive' : 'secondary'}
                      className="ml-2 text-xs"
                    >
                      {item.badge}
                    </Badge>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </Link>
    )
  }

  const SectionHeader = ({ section, isOpen }) => (
    <motion.div
      className="flex items-center justify-between p-2 cursor-pointer group"
      onClick={() => toggleSection(section.id)}
      whileHover={{ x: 2 }}
    >
      <AnimatePresence>
        {isOpen && (
          <motion.span
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider"
          >
            {section.title}
          </motion.span>
        )}
      </AnimatePresence>

      {isOpen && (
        <motion.div
          animate={{ rotate: expandedSections[section.id] ? 90 : 0 }}
          transition={{ duration: 0.2 }}
        >
          <ChevronRight className="h-4 w-4 text-gray-400 dark:text-gray-500 group-hover:text-gray-600 dark:group-hover:text-gray-300" />
        </motion.div>
      )}
    </motion.div>
  )

  return (
    <motion.div
      className="h-full bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 flex flex-col transition-colors duration-200"
      initial={false}
      animate={{ width: isOpen ? '280px' : '80px' }}
      transition={{ duration: 0.3, ease: 'easeInOut' }}
    >
      {/* 用戶資訊區域 */}
      <div className="p-4 border-b border-gray-200 dark:border-gray-700">
        <AnimatePresence>
          {isOpen ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex items-center space-x-3"
            >
              <div className="w-10 h-10 bg-gradient-to-br from-green-400 to-blue-500 rounded-full flex items-center justify-center">
                <span className="text-white font-medium">
                  {currentUser.username.charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                  {currentUser.username}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-300 truncate font-medium">
                  {getUserRoleDisplay(currentUser.role)}
                </p>
              </div>
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex justify-center"
            >
              <div className="w-10 h-10 bg-gradient-to-br from-green-400 to-blue-500 rounded-full flex items-center justify-center">
                <span className="text-white font-medium">
                  {currentUser.username.charAt(0).toUpperCase()}
                </span>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* 快速統計 */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="p-4 border-b border-gray-200 dark:border-gray-700"
          >
            <div className="space-y-3">
              {quickStats.map((stat, index) => (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/80 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600/80 transition-colors"
                >
                  <div className="flex items-center space-x-2">
                    <stat.icon className={`h-4 w-4 ${stat.color}`} />
                    <span className="text-xs text-gray-600 dark:text-gray-200 font-medium">{stat.label}</span>
                  </div>
                  <span className={`text-xs font-bold ${stat.color}`}>
                    {stat.value}
                  </span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 導航選單 */}
      <div className="flex-1 overflow-y-auto p-4 space-y-6">
        {menuSections.map((section) => (
          <div key={section.id}>
            <SectionHeader section={section} isOpen={isOpen} />

            <AnimatePresence>
              {expandedSections[section.id] && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="space-y-1 mt-2"
                >
                  {section.items.map((item) => (
                    <SidebarItem key={item.path} item={item} isOpen={isOpen} />
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        ))}
      </div>

      {/* 底部狀態指示器 */}
      <div className="p-4 border-t border-gray-200 dark:border-gray-700">
        <AnimatePresence>
          {isOpen ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex items-center space-x-2 p-2 bg-green-50 dark:bg-green-900/30 rounded-lg"
            >
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              <span className="text-xs text-green-700 dark:text-green-400 font-medium">{'系統運行正常'}</span>
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex justify-center"
            >
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  )
}

export default Sidebar

